import * as React from 'react';
import { useAuth } from './AuthContext';
import { useSubscription } from './SubscriptionContext';
import {
  checkAvailableRuns,
  initializeUserUsage,
  canUserRunAnalysis,
  getUserUsage,
} from '@/services/usageService';
import { subscribeToUsageUpdates, type UsageData } from '@/services/stripeService';
import { isAdminUser } from '@/services/adminService';
import { createLogger } from '@/services/loggingService';

const logger = createLogger('UsageContext');

// FREE MODE: Set to true to make the app completely free (unlimited runs)
// Toggle this to false when ready to enable usage limits
const FREE_MODE = true;

interface UsageContextValue {
  usage: UsageData | null;
  isLoading: boolean;
  totalRunsAvailable: number;
  freeRunsRemaining: number;
  paidRunsRemaining: number;
  hasRuns: boolean;
  canRunAnalysis: boolean;
  isUnlimited: boolean; // true if user has active subscription
  refreshUsage: () => Promise<void>;
  checkCanRun: () => Promise<{ canRun: boolean; reason: string; runsRemaining?: number }>;
}

const UsageContext = React.createContext<UsageContextValue | undefined>(undefined);

export const UsageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { isSubscribed } = useSubscription();
  const [usage, setUsage] = React.useState<UsageData | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  
  const isAdmin = React.useMemo(() => isAdminUser(user), [user]);

  // Initialize usage for new authenticated users
  React.useEffect(() => {
    const initUsage = async () => {
      if (!user) {
        setUsage(null);
        setIsLoading(false);
        return;
      }

      // If user has active subscription, they don't need usage tracking
      if (isSubscribed) {
        setUsage(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        
        // Check if usage document exists
        const existingUsage = await getUserUsage(user.uid);
        
        if (!existingUsage) {
          // Initialize with 3 free runs for new users
          await initializeUserUsage(user.uid);
        }
      } catch (error) {
        logger.error('Error initializing usage', { error: error instanceof Error ? error.message : String(error) });
      } finally {
        setIsLoading(false);
      }
    };

    initUsage();
  }, [user, isSubscribed]);

  // Subscribe to real-time usage updates for non-subscribers
  React.useEffect(() => {
    if (!user || isSubscribed) {
      setUsage(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    
    const unsubscribe = subscribeToUsageUpdates(user.uid, (usageData) => {
      setUsage(usageData);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [user, isSubscribed]);

  const totalRunsAvailable = React.useMemo(() => {
    if (FREE_MODE) return Infinity;
    if (isAdmin) return Infinity;
    if (isSubscribed) return Infinity;
    if (!usage) return 0;
    return (usage.freeRunsRemaining || 0) + (usage.paidRunsRemaining || 0);
  }, [usage, isSubscribed, isAdmin]);

  const freeRunsRemaining = React.useMemo(() => {
    if (FREE_MODE) return Infinity;
    if (isAdmin) return Infinity;
    if (isSubscribed) return Infinity;
    return usage?.freeRunsRemaining || 0;
  }, [usage, isSubscribed, isAdmin]);

  const paidRunsRemaining = React.useMemo(() => {
    if (FREE_MODE) return Infinity;
    if (isAdmin) return Infinity;
    if (isSubscribed) return Infinity;
    return usage?.paidRunsRemaining || 0;
  }, [usage, isSubscribed, isAdmin]);

  const hasRuns = React.useMemo(() => {
    if (FREE_MODE) return true;
    if (isAdmin) return true;
    if (isSubscribed) return true;
    return totalRunsAvailable > 0;
  }, [totalRunsAvailable, isSubscribed, isAdmin]);

  const canRunAnalysis = React.useMemo(() => {
    if (FREE_MODE) return true;
    if (isAdmin) return true;
    if (isSubscribed) return true;
    return hasRuns;
  }, [hasRuns, isSubscribed, isAdmin]);

  const isUnlimited = React.useMemo(() => {
    return FREE_MODE || isAdmin || isSubscribed;
  }, [isSubscribed, isAdmin]);

  const refreshUsage = React.useCallback(async () => {
    if (!user || isSubscribed) return;

    try {
      const available = await checkAvailableRuns(user.uid);
      // The real-time listener will update the state
      logger.debug('Refreshed usage', { available });
    } catch (error) {
      logger.error('Error refreshing usage', { error: error instanceof Error ? error.message : String(error) });
    }
  }, [user, isSubscribed]);

  const checkCanRun = React.useCallback(async () => {
    // In FREE_MODE, everyone can run analysis (even without auth for now)
    if (FREE_MODE) {
      return { canRun: true, reason: 'free_mode', runsRemaining: Infinity };
    }

    if (!user) {
      return { canRun: false, reason: 'not_authenticated' };
    }

    return await canUserRunAnalysis(user.uid, isSubscribed, isAdmin);
  }, [user, isSubscribed, isAdmin]);

  const value = React.useMemo<UsageContextValue>(
    () => ({
      usage,
      isLoading,
      totalRunsAvailable,
      freeRunsRemaining,
      paidRunsRemaining,
      hasRuns,
      canRunAnalysis,
      isUnlimited,
      refreshUsage,
      checkCanRun,
    }),
    [
      usage,
      isLoading,
      totalRunsAvailable,
      freeRunsRemaining,
      paidRunsRemaining,
      hasRuns,
      canRunAnalysis,
      isUnlimited,
      refreshUsage,
      checkCanRun,
    ]
  );

  return <UsageContext.Provider value={value}>{children}</UsageContext.Provider>;
};

export const useUsage = () => {
  const context = React.useContext(UsageContext);
  if (!context) {
    throw new Error('useUsage must be used within a UsageProvider');
  }
  return context;
};

